/* Endpoint de perfil/tier do usuário */
import { fetchWithAuth, getStoredJwt } from './client';
import type { AIProfile, UserTierResponse } from '../types';

/**
 * Recupera tier e créditos do usuário autenticado.
 * Backend: GET /api/user/tier
 */
export async function getUserTier(): Promise<UserTierResponse> {
  const response = await fetchWithAuth('/api/user/tier', {
    method: 'GET',
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? 'Erro ao buscar tier');
  }

  return response.json();
}

/* ------------------------------------------------------------------------- */
/* Perfil persistente da IA — GET/PUT /api/ai/profile (backend Fase 13.D)     */
/* ------------------------------------------------------------------------- */

/**
 * Perfil server-side — espelha `MedicoProfile` do worker
 * (backend/cloudflare-worker/src/types.ts + ai-profile-v2.ts).
 *
 * ATENÇÃO: o PUT do backend substitui o registro INTEIRO no KV.
 * Campos que a web NÃO edita (learnings, regras_gerais, templates,
 * settings.detalhe_resposta) DEVEM ser preservados via merge antes do PUT
 * — ver `mergeWebProfileIntoServer`.
 */
export interface ServerMedicoProfile {
  /** Especialidade clínica do médico */
  specialty: string;
  /** Preferências de idioma e nível de detalhe */
  settings: {
    idioma: string;
    detalhe_resposta: 'conciso' | 'completo';
  };
  /** Áreas de interesse */
  topics: string[];
  /** Restrições de conduta */
  constraints: string[];
  /** Estilo de comunicação preferido */
  communication_style: string;
  /** Aprendizados da IA sobre o médico — a web NÃO edita, só preserva */
  learnings: string[];
  /** Texto livre opcional (feedback plantão) — só a extensão edita */
  regras_gerais?: string;
  /** Templates de documentos opcionais (brief v4.0 §6.6) — só a extensão edita */
  templates?: {
    receita?: string;
    atestado?: string;
    laudo_previdencia?: string;
    evolucao?: string;
  };
}

/** Regex de idioma aceito pelo backend (mesma de `isValidIdiomaCode`) */
const IDIOMA_REGEX = /^[a-z]{2}(-[A-Z]{2})?$/;

/**
 * Busca o perfil no backend. Backend: GET /api/ai/profile.
 * Retorna `null` se a resposta não for ok (caller trata como "sem server").
 * Lança ChatError em 401/offline (fetchWithAuth) — caller deve capturar.
 */
export async function fetchProfile(): Promise<ServerMedicoProfile | null> {
  const response = await fetchWithAuth('/api/ai/profile', { method: 'GET' });
  if (!response.ok) return null;
  const body = (await response.json()) as { profile?: ServerMedicoProfile };
  return body.profile ?? null;
}

/**
 * Persiste o perfil no backend. Backend: PUT /api/ai/profile.
 * O userId vem do JWT Bearer (injetado pelo fetchWithAuth).
 * Retorna `true` se salvou; `false` em erro de validação/tamanho (400/413).
 */
export async function saveProfile(profile: ServerMedicoProfile): Promise<boolean> {
  const response = await fetchWithAuth('/api/ai/profile', {
    method: 'PUT',
    body: JSON.stringify({ profile }),
  });
  return response.ok;
}

/** Divide textarea livre em array — trim em cada item, descarta vazios */
function splitLista(texto: string, separador: RegExp): string[] {
  return texto
    .split(separador)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

/**
 * Converte perfil server → shape web (só os campos que a web edita).
 * topics vira lista separada por vírgula; constraints por ponto-e-vírgula
 * (mesmos separadores usados na ida em `mergeWebProfileIntoServer`).
 */
export function serverProfileToWeb(server: ServerMedicoProfile): AIProfile {
  return {
    specialty: server.specialty,
    idioma: server.settings.idioma,
    communicationStyle: server.communication_style,
    topicsOfInterest: server.topics.join(', '),
    constraints: server.constraints.join('; '),
  };
}

/**
 * Mergeia os campos editados na web SOBRE o perfil server existente,
 * preservando learnings/regras_gerais/templates/detalhe_resposta —
 * obrigatório porque o PUT do backend substitui o registro inteiro.
 */
export function mergeWebProfileIntoServer(
  web: AIProfile,
  server: ServerMedicoProfile | null,
): ServerMedicoProfile {
  const base: ServerMedicoProfile = server ?? {
    specialty: '',
    settings: { idioma: 'pt-BR', detalhe_resposta: 'completo' },
    topics: [],
    constraints: [],
    communication_style: '',
    learnings: [],
  };
  return {
    ...base,
    specialty: web.specialty.trim(),
    settings: {
      ...base.settings,
      // Defensivo: localStorage antigo pode conter código inválido → fallback
      idioma: IDIOMA_REGEX.test(web.idioma) ? web.idioma : 'pt-BR',
    },
    topics: splitLista(web.topicsOfInterest, /[\n,]+/),
    constraints: splitLista(web.constraints, /[\n;]+/),
    communication_style: web.communicationStyle.trim(),
  };
}

/** `true` se o perfil server tem algum campo gerenciado pela web preenchido */
export function serverProfileTemDados(server: ServerMedicoProfile): boolean {
  return (
    server.specialty.trim().length > 0 ||
    server.communication_style.trim().length > 0 ||
    server.topics.length > 0 ||
    server.constraints.length > 0
  );
}

/** `true` se o perfil web (localStorage) tem algum dado além do default */
export function webProfileTemDados(web: AIProfile): boolean {
  return (
    web.specialty.trim().length > 0 ||
    web.communicationStyle.trim().length > 0 ||
    web.topicsOfInterest.trim().length > 0 ||
    web.constraints.trim().length > 0 ||
    web.idioma !== 'pt-BR'
  );
}

/**
 * Push best-effort do perfil web pro backend (no-op se deslogado).
 * Lê o perfil server primeiro pra preservar learnings antes do PUT.
 * NUNCA lança — falha (offline, 401, 413) vira console.warn e a app
 * segue funcionando com o localStorage como fallback.
 */
export async function pushWebProfileToServer(web: AIProfile): Promise<void> {
  if (!getStoredJwt()) return;
  try {
    const server = await fetchProfile();
    const merged = mergeWebProfileIntoServer(web, server);
    const ok = await saveProfile(merged);
    if (!ok) {
      console.warn('[perfil] PUT /api/ai/profile rejeitado — mantendo localStorage');
    }
  } catch (err) {
    console.warn('[perfil] sync perfil→backend falhou — mantendo localStorage', err);
  }
}
